import { FC, KeyboardEvent, useState } from 'react';
import { Control, Controller, FieldError } from 'react-hook-form';
import { StanBadge } from '@/components';
import { cn } from '@/lib';
import type { AutomationFormData } from '../../validations/automationForm';
import IcClose from '@/assets/icons/ic-x-close.svg';

interface KeywordInputProps {
    control: Control<AutomationFormData>;
    error?: FieldError;
}

export const KeywordInput: FC<KeywordInputProps> = ({ control, error }) => {
    const [inputValue, setInputValue] = useState('');

    const handleKeyDown = (
        e: KeyboardEvent<HTMLInputElement>,
        currentKeywords: string[],
        onChange: (keywords: string[]) => void
    ) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const trimmedValue = inputValue.trim();

            if (trimmedValue && currentKeywords.length < 5) {
                if (!currentKeywords.includes(trimmedValue)) {
                    onChange([...currentKeywords, trimmedValue]);
                    setInputValue('');
                }
            }
        } else if (e.key === 'Backspace' && !inputValue && currentKeywords.length > 0) {
            onChange(currentKeywords.slice(0, -1));
        }
    };

    const removeKeyword = (index: number, currentKeywords: string[], onChange: (keywords: string[]) => void) => {
        onChange(currentKeywords.filter((_, i) => i !== index));
    };

    return (
        <Controller
            name='keywords'
            control={control}
            render={({ field }) => (
                <div className='md:pt-2'>
                    <div className='mb-2'>
                        <label htmlFor='keyword-input' className='text-label-sm-bold block max-md:hidden'>
                            Keywords or Phrases *
                        </label>
                        <p className='text-label-xs-regular text-gray md:text-body-sm'>
                            Add up to 5 keywords or phrases
                            <span className='max-md:hidden'> that will trigger this automation</span>.{' '}
                            <span className='max-md:hidden'>Press Enter to add.</span>
                        </p>
                    </div>

                    <div
                        className={cn(
                            'flex min-h-12 flex-wrap items-center gap-2 rounded-md border px-2.5 py-1.5 transition-all duration-200 md:px-4 md:py-3',
                            'bg-canvas border-disabled',
                            'hover:border-brand-main',
                            'focus-within:border-brand-main focus-within:shadow-material-focus-glow-default',
                            error && 'bg-negative-subtle border-negative-intense',
                            error && 'hover:border-negative-bold',
                            error &&
                                'focus-within:border-negative-intense focus-within:shadow-material-focus-glow-error'
                        )}
                    >
                        {/* Keywords Display */}
                        {field.value.length > 0 && (
                            <div className='flex flex-wrap gap-2'>
                                {field.value.map((keyword, index) => (
                                    <button
                                        key={keyword}
                                        type='button'
                                        onClick={() => removeKeyword(index, field.value, field.onChange)}
                                        aria-label={`Remove ${keyword}`}
                                        className='ring-brand-main/50 max-w-full cursor-pointer rounded-md transition hover:scale-105 hover:ring-2 focus-visible:scale-105 focus-visible:ring-2 active:scale-95'
                                    >
                                        <StanBadge variant='primary' icon={<IcClose className='size-4' />} size='sm'>
                                            {keyword}
                                        </StanBadge>
                                    </button>
                                ))}
                            </div>
                        )}

                        {field.value.length < 5 && (
                            <input
                                id='keyword-input'
                                type='text'
                                value={inputValue}
                                onChange={e => setInputValue(e.target.value)}
                                onKeyDown={e => handleKeyDown(e, field.value, field.onChange)}
                                disabled={field.value.length >= 5}
                                placeholder='Enter keyword, e.g. hello'
                                className={cn(
                                    'flex-1 border-none bg-transparent outline-none',
                                    'text-caption-1-regular!',
                                    'placeholder:text-neutral-fg-disabled max-md:placeholder:text-body-sm',
                                    field.value.length >= 5 && 'cursor-not-allowed opacity-50'
                                )}
                            />
                        )}
                    </div>

                    {error && <p className='text-body-xs text-negative mt-1 font-semibold'>{error.message}</p>}
                </div>
            )}
        />
    );
};
